import React from "react";
import _ from 'lodash';

interface IFuncProps {
  root: any;
  item: any;
  targetId: any;
  layerName: any;
}

interface IFuncDeleteProps {
  root: any;
  targetId: any;
  layerName: any;
}

interface ICreateInitialData {
  eixos_tematicos: {
    itens: any[];
  };
}

interface IHierarchyPreparationOfPlans {
  createInitialData: (layer: any) => ICreateInitialData;
  ClearInitialData: (root: any, id: any) => void;
  addRootLayer(root: any, newLayer: string): any;
  findNewItemsById(firstArray: any[], id: any): any[];
  findNewItemsWithId(firstArray: any, secondArray: any): any;
  insertItemById: ({ item, layerName, root, targetId }: IFuncProps) => any;
  updateItemById: ({ item, layerName, root, targetId }: IFuncProps) => any;
  deleteItemById: ({ layerName, root, targetId }: IFuncDeleteProps) => any;
  checkLayerExists: ({ root, targetId, layerName }: IFuncDeleteProps) => boolean;
}

export const HierarchyPreparationOfPlansContext = React.createContext({} as IHierarchyPreparationOfPlans);

interface IHierarchyPreparationOfPlansProps {
  children: React.ReactNode;
}

export const HierarchyPreparationOfPlansProvider: React.FC<IHierarchyPreparationOfPlansProps> = ({ children }) => {
  const deepClone = (obj: any): any => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(deepClone);

    const clone: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clone[key] = deepClone(obj[key]);
      }
    }
    return clone;
  };

  const traverseTree = (node: any, callback: any) => {
    if (!node || typeof node !== 'object') return;

    callback(node);

    for (let key in node) {
      if (Array.isArray(node[key])) {
        node[key].map((item: any) => traverseTree(item, callback));
      } else if (typeof node[key] === 'object') {
        traverseTree(node[key], callback);
      }
    }
  }

  const insertItemById = ({ item, layerName, root, targetId }: IFuncProps) => {
    if (!root || typeof root !== 'object' || !targetId || !item || !layerName) return root;

    const clonedRoot = deepClone(root); // Cria uma cópia profunda do root
    let idFound = false;

    // Percorre a árvore procurando pelo ID
    traverseTree(clonedRoot, (node: any) => {
      if (node.id === targetId && node.relacionamento && node.relacionamento[layerName]) {
        idFound = true;
        node.relacionamento[layerName].itens.push(item);
      }
    });

    // Se o ID não foi encontrado, cria a estrutura no clone
    if (!idFound) {
      let currentNode = clonedRoot;
      const keys = layerName.split('.');

      // Cria a estrutura se não existir
      for (let key of keys) {
        currentNode[key] = currentNode[key] || {};
        currentNode = currentNode[key];
      }

      // Adiciona o novo item
      currentNode.itens = currentNode.itens || [];
      currentNode.itens.push(item);
    }

    return clonedRoot; // Retorna o objeto atualizado
  };

  const updateItemById = ({ item, layerName, root, targetId }: IFuncProps) => {
    if (!root || typeof root !== 'object' || !targetId || !item || !layerName) {
      return root; // Retorna o objeto original se a validação falhar
    }

    const clonedRoot = deepClone(root); // Faz uma cópia profunda do root
    let idFound = false;

    // Percorre a árvore procurando pelo ID no objeto clonado
    traverseTree(clonedRoot, (node: any) => {
      if (node.id === targetId && node.relacionamento) {
        idFound = true;
        node.relacionamento = { ...node.relacionamento, ...item }; // Atualiza o relacionamento no clone
      }
    });

    return clonedRoot; // Retorna o objeto clonado com a modificação
  };

  const deleteItemById = ({ layerName, root, targetId }: IFuncDeleteProps) => {
    if (!root || typeof root !== 'object' || !targetId || !layerName) {
      return root; // Retorna o objeto original se a validação falhar
    }

    const clonedRoot = deepClone(root); // Faz uma cópia profunda do root
    let idDeleted = false;

    // Percorre a árvore no objeto clonado
    traverseTree(clonedRoot, (node: any) => {
      if (node.relacionamento && node.relacionamento[layerName]) {
        const index = node.relacionamento[layerName].itens.findIndex((item: any) => item.id === targetId);
        if (index !== -1) {
          node.relacionamento[layerName].itens.splice(index, 1); // Remove o item no clone
          idDeleted = true;
          return true; // Retorna true para interromper a iteração
        }
      }
    });

    return clonedRoot; // Retorna o objeto clonado com a modificação
  };

  const createInitialData = (layer: any) => {
    if (layer == 'sintese_diagnostico') {
      return {
        sintese_diagnostico: {
          itens: []
        },
        eixos_tematicos: {
          itens: []
        }
      }
    }

    return {
      eixos_tematicos: {
        itens: []
      }
    };
  };

  const ClearInitialData = (root: any, id: any) => {
    const items = root.eixos_tematicos.itens.filter((item: any) => item.id != id);
    root.eixos_tematicos.itens = items;
    return root;
  };

  function findNewItemsById(firstArray: any[], id: any) {
    return firstArray.filter(item => item.id == id);
  }

  function findNewItemsWithId(firstArray: any, secondArray: any) {
    if (Array.isArray(firstArray) && Array.isArray(secondArray)) {
      return secondArray?.filter((secondItem: any) => {
        const firstItem = firstArray?.find((firstItem: any) => firstItem?.texto === secondItem?.texto);
        return firstItem && !firstItem?.id && secondItem?.id;
      });
    }
  }

  const checkLayerExists = ({ layerName, root, targetId }: IFuncDeleteProps): boolean => {
    if (!root || typeof root !== 'object' || !targetId || !layerName) {
      return root; // Retorna o objeto original se a validação falhar
    }

    const clonedRoot = deepClone(root); // Faz uma cópia profunda do root
    let findItem = false;

    // Percorre a árvore no objeto clonado
    traverseTree(clonedRoot, (node: any) => {
      if (node.relacionamento && node.relacionamento[layerName]) {
        const index = node.relacionamento[layerName].itens.findIndex((item: any) => item.id === targetId);
        if (index !== -1) {
          findItem = true;
          return true; // Retorna true para interromper a iteração
        }
      }
    });

    return findItem; 
  
  };

  function addRootLayer(root: any, newLayer: string) {
    // Verifica se o objeto raiz e o nome da nova camada são válidos
    if (!root || typeof root !== 'object' || !newLayer || typeof newLayer !== 'string') {
      return root;
    }

    const clonedRoot = deepClone(root);

    // Verifica se a camada já existe para evitar sobrescrever
    if (!clonedRoot.hasOwnProperty(newLayer)) {
      clonedRoot[newLayer] = {
        itens: []
      }; // Adiciona a nova camada na primeira camada
    }

    return clonedRoot; // Retorna o objeto atualizado
  }


  const value = {
    addRootLayer,
    deleteItemById,
    insertItemById,
    updateItemById,
    findNewItemsById,
    checkLayerExists,
    ClearInitialData,
    createInitialData,
    findNewItemsWithId,
  } as IHierarchyPreparationOfPlans;

  return <HierarchyPreparationOfPlansContext.Provider value={value}>{children}</HierarchyPreparationOfPlansContext.Provider>;
};